import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, Images } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea, AdminSelect, EmptyState, StatusPill } from "@/admin/components/ui";

const empty = { name: "", description: "", display_order: 0, status: "active" };

export default function AdminSubfolders() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | folder.id
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    adminApi.get(`/admin/events/${id}/subfolders`)
      .then((r) => { setEvent(r.data.event); setFolders(r.data.subfolders); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  const startNew = () => { setForm({ ...empty, display_order: folders.length }); setEditing("new"); };
  const startEdit = (f) => { setForm({ ...empty, ...f }); setEditing(f.id); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Subfolder name is required."); return; }
    try {
      if (editing === "new") {
        await adminApi.post(`/admin/events/${id}/subfolders`, form);
        toast.success("Subfolder created.");
      } else {
        await adminApi.put(`/admin/subfolders/${editing}`, form);
        toast.success("Subfolder updated.");
      }
      cancel();
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete subfolder "${f.name}"?`)) return;
    try {
      await adminApi.delete(`/admin/subfolders/${f.id}`);
      toast.success("Subfolder deleted.");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  if (loading) return <p className="text-sm text-white/40">Loading…</p>;

  return (
    <div data-testid="page-admin-subfolders" className="space-y-6">
      <SEO title="AdminSubfolders · WelkomBiz Photography" noindex />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <Link to="/admin/events" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Events
          </Link>
          <h2 className="font-display text-2xl sm:text-3xl text-chrome mt-3">{event?.title}</h2>
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">{event?.category} · {event?.date}</p>
        </div>
        <button data-testid="subfolder-add-btn" onClick={startNew} className="btn-primary !py-2.5 !px-5 !text-[11px]">
          <Plus className="w-3.5 h-3.5" /> Add Subfolder
        </button>
      </div>

      {editing && (
        <Panel testId="subfolder-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AdminInput data-testid="subfolder-input-name" label="Subfolder Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rugby, Ceremony, Reception…" />
            <AdminInput data-testid="subfolder-input-order" label="Display Order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} />
            <AdminTextarea data-testid="subfolder-input-desc" label="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" />
            <AdminSelect data-testid="subfolder-input-status" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active" className="bg-black">Active</option>
              <option value="hidden" className="bg-black">Hidden</option>
            </AdminSelect>
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            <button data-testid="subfolder-save-btn" onClick={save} className="btn-primary !py-2.5 !px-5 !text-[11px]">
              <Save className="w-3.5 h-3.5" /> Save Subfolder
            </button>
            <button onClick={cancel} className="btn-ghost !py-2.5 !px-5 !text-[11px]">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </Panel>
      )}

      {folders.length === 0 ? (
        <EmptyState testId="subfolders-empty" title="No subfolders yet" message="Create subfolders like Rugby, Ceremony, Reception, Stage Photos to organise this event." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map((f) => (
            <div key={f.id} data-testid={`subfolder-card-${f.id}`} className="glass chrome-border rounded-md p-5 lift">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-xl text-chrome truncate">{f.name}</p>
                  <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">#{f.display_order} · {f.photo_count ?? 0} photos</p>
                </div>
                <StatusPill status={f.status} />
              </div>
              {f.description && <p className="text-xs text-white/55 mt-3 font-body line-clamp-3">{f.description}</p>}
              <div className="flex items-center gap-2 mt-4">
                <Link to={`/admin/events/${id}/folders/${f.id}/photos`} data-testid={`subfolder-photos-${f.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] tracking-luxe uppercase py-2 rounded-md border border-transparent bg-gradient-to-r from-[#ff2e93]/15 to-[#18d3ff]/15 chrome-border text-white">
                  <Images className="w-3 h-3" /> Photos ({f.photo_count ?? 0})
                </Link>
                <button data-testid={`subfolder-edit-${f.id}`} onClick={() => startEdit(f)} className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-luxe uppercase px-3 py-2 rounded-md border border-white/10 text-white/70 hover:text-white">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button data-testid={`subfolder-delete-${f.id}`} onClick={() => remove(f)} className="px-3 py-2 rounded-md border border-white/10 text-[#ff7fc1] hover:bg-white/5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
